import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTransferHistory, retryTransfer } from '../services/api'
import { normalizeTransfers } from '@/utils/normalize'
import { useAuth } from '@/contexts/AuthContext'
import Header from '@/components/Header'

interface Transfer {
  id: string
  transferNo: string
  recipient: string
  amount: number
  description: string
  status: 'pending' | 'processing' | 'success' | 'failed'
  transferMode: 'REAL_TIME' | 'USER_CONFIRM'
  createdAt: string
  wechatTransferId?: string
  errorMessage?: string
}

export default function TransferHistory() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [transfers, setTransfers] = useState<Transfer[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    page: 1,
    limit: 20
  })
  

  useEffect(() => {
    fetchHistory()
  }, [filters, user?.activeMerchantId])

  const fetchHistory = async () => {
    try {
      const response = await getTransferHistory(filters)
      setTransfers(normalizeTransfers(response.data?.transfers))
    } catch (error) {
      console.error('获取转账历史失败:', error)
      alert('获取转账历史失败，请重试')
    } finally {
      setIsLoading(false)
    }
  }

  const canRetry = (t: Transfer) => {
    if (t.status !== 'failed') return false
    const msg = String(t.errorMessage || '')
    return /资金不足|余额不足|NOT_ENOUGH|原单号发起重试/.test(msg)
  }

  const handleRetry = async (t: Transfer) => {
    try {
      const ok = window.confirm(`确认重试该转账单号：${t.transferNo}？`)
      if (!ok) return
      await retryTransfer({ id: t.id })
      await fetchHistory()
      navigate(`/transfer/status/${t.id}`)
    } catch (error: any) {
      alert(error?.message || '重试失败')
    }
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200',
      processing: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200',
      success: 'bg-green-100 text-green-800 ring-1 ring-green-200',
      failed: 'bg-red-100 text-red-800 ring-1 ring-red-200'
    }
    const texts = {
      pending: '待处理',
      processing: '处理中',
      success: '成功',
      failed: '失败'
    }
    return (
      <span className={`inline-flex items-center px-2.5 h-6 rounded-md text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {texts[status as keyof typeof texts]}
      </span>
    )
  }

  const exportData = () => {
    const csvContent = [
      ['转账单号', '收款人', '金额', '状态', '转账方式', '创建时间', '备注'],
      ...transfers.map(t => [
        t.transferNo,
        t.recipient,
        t.amount.toFixed(2),
        t.status === 'success' ? '成功' : t.status === 'failed' ? '失败' : t.status === 'processing' ? '处理中' : '待处理',
        t.transferMode === 'REAL_TIME' ? '实时到账' : '用户确认',
        new Date(t.createdAt).toLocaleString('zh-CN'),
        t.description
      ])
    ].map(row => row.join(',')).join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `转账记录_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="转账历史" backTo="/dashboard" right={<button onClick={exportData} className="bg-wechat-green hover:bg-wechat-dark text-white px-4 py-2 rounded-md text-sm font-medium">导出数据</button>} />

      {/* Filters */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg px-6 py-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">开始日期</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="block w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-wechat-green focus:border-wechat-green text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">结束日期</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="block w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-wechat-green focus:border-wechat-green text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">状态</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="block w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-wechat-green focus:border-wechat-green text-sm"
              >
                <option value="">全部</option>
                <option value="pending">待处理</option>
                <option value="processing">处理中</option>
                <option value="success">成功</option>
                <option value="failed">失败</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ startDate: '', endDate: '', status: '', page: 1, limit: 20 })}
                className="w-full h-10 bg-wechat-green hover:bg-wechat-dark text-white px-4 rounded-md text-sm font-medium"
              >
                重置
              </button>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => fetchHistory()}
                className="w-full h-10 bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 rounded-md text-sm font-medium border border-gray-300"
              >
                查询
              </button>
            </div>
          </div>
        </div>

        {/* Transfer List */}
        {isLoading ? (
          <div className="bg-white shadow rounded-lg px-6 py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-wechat-green mx-auto"></div>
            <p className="mt-4 text-gray-500">加载中...</p>
          </div>
        ) : transfers.length === 0 ? (
          <div className="bg-white shadow rounded-lg px-6 py-12 text-center">
            <p className="text-gray-500">暂无转账记录</p>
          </div>
        ) : (
          <div className="bg-white shadow-sm border border-gray-200 overflow-hidden sm:rounded-lg">
            <ul className="divide-y divide-gray-200">
              {transfers.map((transfer) => (
                <li key={transfer.id}>
                  <div className="px-6 py-5 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900">
                          {transfer.recipient}
                        </p>
                        <div className="ml-2 flex-shrink-0 flex">
                          {getStatusBadge(transfer.status)}
                        </div>
                      </div>
                      <div className="mt-2 sm:flex sm:justify-between">
                        <div className="sm:flex">
                          <p className="flex items-center text-sm text-gray-500">
                            单号: {transfer.transferNo}
                          </p>
                          <p className="mt-1 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                            {transfer.transferMode === 'REAL_TIME' ? '实时到账' : '用户确认'}
                          </p>
                        </div>
                        <div className="mt-1 text-sm text-gray-500 sm:mt-0">
                          <p>
                            金额: <span className="font-medium text-gray-900">¥{transfer.amount.toFixed(2)}</span>
                          </p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          {transfer.description}
                        </p>
                        {transfer.status === 'failed' && transfer.errorMessage && (
                          <p className="mt-1 text-xs text-red-600">{transfer.errorMessage}</p>
                        )}
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-400">
                          {new Date(transfer.createdAt).toLocaleString('zh-CN')}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => navigate(`/transfer/status/${transfer.id}`)}
                        className="text-wechat-green hover:text-wechat-dark text-sm font-medium"
                      >
                        查看详情
                      </button>
                      {canRetry(transfer) && (
                        <button
                          onClick={() => handleRetry(transfer)}
                          className="ml-3 text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          重试
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
